import { PlayButton } from "@southfields-digital/mxmz-react-ui";
import classNames from "classnames";
import { useEffect } from "react";

import type { MainClock } from "src/types/matchControl/clock";
import type { Control } from "src/types/matchControl/control";

import { ClockStart, ExtraIn, TimeIn } from "../../components";
import TimeAdjustButtons from "../../components/TimeAdjustButtons";
import { useTimer } from "../../hooks";
import styles from "../../MatchControl.module.scss";

const MAIN_CLOCK_ID = "clock";
const EXTRA_TIME_ID = "extraTime";

type ClockRowProps = {
	mainClock: MainClock;
	matchControlGraphics: Control[];
	isCompact: boolean;
	handleStartTime: (clock: MainClock) => void;
	handleStopTime: (clock: MainClock) => void;
	handleDispatchControl: (control: Control | undefined) => void;
	handleChangeTime: (clock: MainClock, time: number) => void;
	handleChangeExtraTime: (clock: MainClock, amount: number) => void;
};

const ClockRow = ({
	mainClock,
	matchControlGraphics,
	isCompact,
	handleStartTime,
	handleStopTime,
	handleDispatchControl,
	handleChangeTime,
	handleChangeExtraTime,
}: ClockRowProps) => {
	const { time: mainClockTime, setTimer } = useTimer(
		10,
		mainClock.clockTime,
		mainClock.clockCountDirection,
	);

	// Update the timer with server-time offset whenever the clock state changes
	useEffect(() => {
		if (!mainClock) return;

		const clock = mainClock;
		const serverTimeOffset = clock.serverTimeUpdate
			? new Date(clock.serverTimeUpdate).getTime() - Date.now()
			: 0;

		const startedAt = clock.clockTimeUpdatedAt
			? new Date(clock.clockTimeUpdatedAt).getTime() - serverTimeOffset
			: null;

		setTimer(clock.clockTime || 0, startedAt ? new Date(startedAt) : null);
	}, [mainClock, mainClock?.clockTimeUpdatedAt, mainClock?.clockTime]);

	const clockControls = (
		<div className={styles.ExtraInWrapper}>
			<ClockStart
				isRunning={Boolean(mainClock.clockTimeUpdatedAt)}
				handleStartTimer={() => handleStartTime(mainClock)}
				handleStopTimer={() => handleStopTime(mainClock)}
				minimize={isCompact}
			/>
			<PlayButton
				isRunning={Boolean(
					matchControlGraphics?.find(
						(control) => control.controlId === MAIN_CLOCK_ID && control.live,
					),
				)}
				onClick={() =>
					handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === MAIN_CLOCK_ID))
				}
				className={classNames(
					"w-full place-content-center",
					isCompact ? "!h-7 !text-sm" : "!h-9 !text-lg",
				)}
			>
				CLOCK
			</PlayButton>
		</div>
	);

	const timeIn = (
		<TimeIn
			time={mainClockTime ?? 0}
			extraTime={mainClock.extraTime}
			precision="minutes"
			handleChangeTime={(time) => handleChangeTime(mainClock, mainClock.clockTime + time)}
			isCompact={isCompact}
		/>
	);

	const timeAdjustButtons = (
		<TimeAdjustButtons
			onAdd={(amount) => handleChangeTime(mainClock, mainClock.clockTime + amount * 1000)}
			onSubtract={(amount) => handleChangeTime(mainClock, mainClock.clockTime - amount * 1000)}
			isCompact={isCompact}
		/>
	);

	const extraIn = (
		<ExtraIn
			isRunning={matchControlGraphics?.some(
				(control) => control.controlId === EXTRA_TIME_ID && control.live,
			)}
			handleChangeExtraTime={(amount) =>
				handleChangeExtraTime(mainClock, (mainClock.extraTime ?? 0) + amount)
			}
			handleToggleIsExtraTimeLive={() =>
				handleDispatchControl(matchControlGraphics?.find((c) => c.controlId === EXTRA_TIME_ID))
			}
			extraTime={mainClock.extraTime ?? 0}
			isCompact={isCompact}
		/>
	);

	return (
		<>
			{clockControls}
			<div className="flex flex-col gap-4">
				{timeIn}
				{timeAdjustButtons}
			</div>
			{extraIn}
		</>
	);
};

export default ClockRow;
